import { useLocale as useLocaleR40 } from '@/lib/locale';
import React, { useEffect, useMemo, useState } from 'react';
import Seo from '@/components/Seo';
import { useNavigate, useParams } from 'react-router-dom';
import api, { fmtPrice } from '@/lib/api';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { handleStripeDecline } from '@/lib/paymentDecline';
import { PaymentDeclineNotice } from '@/components/PaymentDeclineNotice';
import { Loader2, Lock, CheckCircle2, AlertTriangle, Repeat, Package as PackageIcon } from 'lucide-react';

/**
 * EnrollmentInvitePage
 * --------------------
 * Public landing page parents reach from the email a coach sends when
 * they're enrolled into a Class/Package and don't have a card on file.
 *
 * Flow:
 *   1. Fetch `/api/public/enrollment-invite/{token}` → returns summary +
 *      Stripe client_secret (PaymentIntent for one_time, SetupIntent for
 *      recurring_monthly) scoped to the coach's connected account.
 *   2. Mount Stripe Elements with `stripeAccount` so the card is tokenized
 *      against the right account.
 *   3. On submit → `stripe.confirmPayment` / `confirmSetup` → POST our
 *      `.../confirm` endpoint which finalizes the enrollment + creates
 *      the Subscription for recurring items.
 *   4. Show a success state with next-step CTAs (log in / go to portal).
 *
 * Error states:
 *   - 404 invitation not found
 *   - 410 already used / expired
 *   - 503 coach Stripe not enabled
 * Each shown with friendly copy + a "Contact your coach" hint instead of
 * the raw API error.
 */
export default function EnrollmentInvitePage() {
    const { t: tr40 } = useLocaleR40();
    const { token } = useParams();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let alive = true;
        setLoading(true);
        api.get(`/public/enrollment-invite/${token}`)
            .then((r) => { if (alive) setData(r.data); })
            .catch((e) => {
                if (!alive) return;
                const status = e?.response?.status;
                const detail = e?.response?.data?.detail;
                if (status === 404) setError("We couldn't find this invitation. Double-check the link or ask your coach to resend it.");
                else if (status === 410) setError(detail || "This invitation has expired or already been used.");
                else if (status === 503) setError(detail || "Your coach hasn't finished setting up payments yet. Reach out to them directly.");
                else setError(detail || "Something went wrong loading this invitation.");
            })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [token]);

    if (loading) {
        return (
            <div className="flex min-h-dvh items-center justify-center bg-[#f4f7fb]">
                <Loader2 className="h-6 w-6 animate-spin text-[#00d4ff]" />
            </div>
        );
    }
    if (error) {
        return (
            <div className="flex min-h-dvh items-center justify-center bg-[#f4f7fb] p-6">
                <Card className="max-w-md p-6 text-center" data-testid="invite-error-card">
                    <AlertTriangle className="mx-auto mb-3 h-8 w-8 text-[#ff6b35]" />
                    <h1 className="font-display text-lg font-bold text-[#0a1628]">{tr40("We couldn't open this invitation")}</h1>
                    <p className="mt-2 text-sm text-[#1a3a5c]/80">{error}</p>
                </Card>
            </div>
        );
    }
    if (!data) return null;
    return <InviteWithElements data={data} token={token} navigate={navigate} />;
}

function InviteWithElements({ data, token, navigate }) {
    const { t: tr40 } = useLocaleR40();
    // loadStripe is memoized per-publishable-key so we don't re-create the
    // SDK on every render. We MUST pass `stripeAccount` here because the
    // client_secret was issued on the coach's connected account.
    const stripePromise = useMemo(() => {
        if (!data.stripe_publishable_key) return null;
        return loadStripe(data.stripe_publishable_key, { stripeAccount: data.stripe_account_id });
    }, [data.stripe_publishable_key, data.stripe_account_id]);

    const appearance = {
        theme: 'stripe',
        variables: {
            colorPrimary: data.coach?.brand_color || '#00d4ff',
            borderRadius: '10px',
        },
    };

    // Phase 182 — the server detected an earlier successful charge for this
    // invitation and finished the enrollment itself. Show "done" — never
    // mount a payment form on top of a card that was already charged.
    if (data.already_paid) {
        return <InviteSuccess data={data} navigate={navigate} alreadyPaid />;
    }
    if (!stripePromise) {
        return (
            <Card className="m-6 p-6 text-center">
                <AlertTriangle className="mx-auto mb-2 h-6 w-6 text-[#ff6b35]" />
                <p className="text-sm text-[#1a3a5c]">{tr40("Payment isn't available right now (missing Stripe key). Please reach out to your coach.")}</p>
            </Card>
        );
    }
    return (
        <Elements stripe={stripePromise} options={{ clientSecret: data.client_secret, appearance }}>
            <InviteInner data={data} token={token} />
        </Elements>
    );
}

function InviteInner({ data, token }) {
    const { t: tr40 } = useLocaleR40();
    const stripe = useStripe();
    const elements = useElements();
    const navigate = useNavigate();
    const [submitting, setSubmitting] = useState(false);
    const [paymentError, setPaymentError] = useState('');
    const [decline, setDecline] = useState(null);
    const tryAnotherCard = () => {
        setDecline(null); setPaymentError('');
        try { elements?.getElement('payment')?.clear(); } catch { /* noop */ }
        try { elements?.getElement('payment')?.focus(); } catch { /* noop */ }
    };
    const [success, setSuccess] = useState(false);
    const [charged, setCharged] = useState(false);          // Phase 182
    const [finalizeError, setFinalizeError] = useState(''); // Phase 182

    const recurring = data.kind === 'class';
    const kindLabel = recurring ? 'Class (recurring)' : 'Package (one-time)';
    const KindIcon = recurring ? Repeat : PackageIcon;
    const priceSuffix = recurring ? '/month' : '';

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!stripe || !elements) return;
        setSubmitting(true);
        setPaymentError('');
        setDecline(null);
        try {
            // Same page = no redirect. We pass `redirect: 'if_required'` so
            // Stripe only takes us away for 3DS / bank flows.
            const confirmFn = data.intent_type === 'setup' ? stripe.confirmSetup : stripe.confirmPayment;
            const { error, paymentIntent, setupIntent } = await confirmFn({
                elements,
                confirmParams: { return_url: window.location.href },
                redirect: 'if_required',
            });
            if (error) {
                // Phase 178 — plain-English reason + tell the coach.
                setDecline(handleStripeDecline(error, {
                    context: 'invite',
                    instructorId: data?.coach?.id || data?.instructor_id,
                    enrollmentId: data?.enrollment_id || data?.enrollment?.id,
                    amountCents: data?.package?.price_cents,
                    packageName: data?.package?.name,
                    parentEmail: data?.parent_email || data?.parent?.email,
                }));
                setSubmitting(false);
                return;
            }
            // Finalize on the server — creates Subscription for recurring,
            // marks enrollment active for one_time.
            const payload = data.intent_type === 'setup'
                ? { setup_intent_id: setupIntent?.id }
                : { payment_intent_id: paymentIntent?.id };
            // Phase 182 — the card is CHARGED at this point. If our server
            // call hiccups we must never show a "try again" that re-charges.
            // Retry the finalize quietly; if it still fails, say so plainly.
            setCharged(true);
            let lastErr = null;
            for (let attempt = 0; attempt < 3; attempt += 1) {
                try {
                    await api.post(`/public/enrollment-invite/${token}/confirm`, payload);
                    lastErr = null;
                    break;
                } catch (err) {
                    lastErr = err;
                    // 400 = verification says this isn't our payment — don't hammer.
                    if (err?.response?.status === 400) break;
                    await new Promise((r) => setTimeout(r, 1200 * (attempt + 1)));
                }
            }
            if (lastErr) {
                setFinalizeError(lastErr?.response?.data?.detail || 'Your payment went through, but we could not finish the enrollment on our side.');
                return;
            }
            setSuccess(true);
        } catch (err) {
            setPaymentError(err?.response?.data?.detail || 'Something went wrong. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    if (success) {
        return <InviteSuccess data={data} navigate={navigate} />;
    }

    // Phase 182 — charged but not finalized: no payment form, no retry button.
    if (charged && finalizeError) {
        return (
            <div className="min-h-dvh bg-[#f4f7fb] p-6">
                <Card className="mx-auto max-w-lg p-8 text-center" data-testid="invite-charged-pending-card">
                    <CheckCircle2 className="mx-auto mb-3 h-10 w-10 text-[#00b87a]" />
                    <h1 className="font-display text-xl font-bold text-[#0a1628]">{tr40("Your payment went through")}</h1>
                    <p className="mt-2 text-sm text-[#1a3a5c]/85"> {tr40("Your card was charged for \"")}{data.package.name}{tr40("\" and you'll get a Stripe receipt by email. We hit a snag finishing the enrollment on our side —")} <strong>{tr40("please don't pay again")}</strong>{tr40(". Your coach will see the payment and your spot will be confirmed automatically; if it isn't within a few hours, just reply to your coach.")} </p>
                    <p className="mt-2 text-xs text-[#1a3a5c]/60" data-testid="invite-charged-pending-detail">{finalizeError}</p>
                    <Button
                        onClick={() => navigate('/parent/dashboard')}
                        className="sd-cta sd-focus mt-5 h-11 rounded-xl"
                        data-testid="invite-charged-go-to-dashboard"
                    > {tr40("Go to your dashboard")} </Button>
                </Card>
            </div>
        );
    }

    return (
        <div className="min-h-dvh bg-[#f4f7fb] p-4 sm:p-6">
            <Seo
                title={tr40("Complete your enrollment | SplashPad")}
                description={tr40("Finish your swim lesson enrollment and payment securely through SplashPad.")}
                canonical="https://splashpad.pro/enroll"
                noIndex
            />
            <div className="mx-auto max-w-lg space-y-4" data-testid="invite-pay-form">
                {/* Coach branding header */}
                <div className="flex items-center gap-3 px-1">
                    {data.coach.logo_url ? (
                        <img src={data.coach.logo_url} alt={data.coach.display_name} className="h-10 w-10 rounded-full object-cover" />
                    ) : (
                        <div className="h-10 w-10 rounded-full bg-[#00d4ff]/20" />
                    )}
                    <div>
                        <div className="text-xs uppercase tracking-wide text-[#1a3a5c]/70">{tr40("Enrollment invitation")}</div>
                        <div className="font-display text-lg font-bold text-[#0a1628]">{data.coach.display_name || 'Your coach'}</div>
                    </div>
                </div>

                {/* Summary card */}
                <Card className="p-5" data-testid="invite-summary-card">
                    <div className="flex items-start gap-3">
                        <div className="rounded-xl bg-[#00d4ff]/10 p-2.5 text-[#0a1628]"><KindIcon className="h-5 w-5" /></div>
                        <div className="min-w-0 flex-1">
                            <Badge variant="outline" className="border-transparent bg-[#0a1628]/8 text-[#0a1628]">{kindLabel}</Badge>
                            <div className="mt-1 font-display text-lg font-bold text-[#0a1628]">{data.package.name}</div>
                            {data.package.description && (
                                <p className="mt-1 text-sm text-[#1a3a5c]/80">{data.package.description}</p>
                            )}
                            <div className="mt-3 flex items-baseline gap-1">
                                <span className="font-display text-2xl font-bold text-[#0a1628]" data-testid="invite-price">
                                    {fmtPrice(data.package.price_cents, data.package?.currency)}
                                </span>
                                <span className="text-sm text-[#1a3a5c]/70">{priceSuffix}</span>
                            </div>
                            {recurring && (
                                <p className="mt-1 text-xs text-[#1a3a5c]/70"> {tr40("Auto-bills on day")} {data.package.billing_day_of_month} {tr40("of each month. Cancel anytime.")} </p>
                            )}
                        </div>
                    </div>
                </Card>

                {/* Stripe Elements pay form */}
                <Card className="p-5">
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div>
                            <div className="mb-2 flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-[#1a3a5c]/70">
                                <Lock className="h-3 w-3" /> {tr40("Payment details")} </div>
                            <PaymentElement options={{ layout: 'tabs' }} />
                        </div>
                        {decline && (
                            <PaymentDeclineNotice decline={decline} idPrefix="invite-decline" onTryAnother={tryAnotherCard} onRetry={() => setDecline(null)} />
                        )}
                        {paymentError && !decline && (
                            <div className="rounded-lg border border-[#ff6b35]/40 bg-[#fff6f0] p-3 text-sm text-[#0a1628]" data-testid="invite-pay-error">
                                {paymentError}
                            </div>
                        )}
                        {/* Phase 0bv — locked card-on-file consent disclosure. */}
                        <div
                            className="flex items-start gap-2 rounded-lg border border-[#00d4ff]/25 bg-[#e6f9fd] p-3 text-xs text-[#0a1628]"
                            data-testid="invite-card-on-file-notice"
                        >
                            <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#00d4ff]" aria-hidden="true" />
                            <span className="leading-relaxed">
                                <strong>{tr40("Your card will be saved on file.")}</strong>{' '} {tr40("By")} {recurring ? tr40("starting this subscription") : tr40("paying")}{tr40(", you authorize")} {data.coach.display_name || 'your coach'} {tr40("to save this card and charge it for future lessons or packages. Manage saved cards anytime under")} <span className="font-medium">{tr40("Payment methods")}</span> {tr40("in your dashboard.")} </span>
                        </div>
                        <Button
                            type="submit"
                            disabled={!stripe || !elements || submitting}
                            className="sd-cta sd-focus h-12 w-full rounded-xl"
                            data-testid="invite-pay-submit"
                        >
                            {submitting
                                ? <><Loader2 className="mr-1 h-4 w-4 animate-spin" /> {tr40("Confirming…")}</>
                                : recurring
                                    ? <>{tr40("Start subscription —")} {fmtPrice(data.package.price_cents, data.package?.currency)}{tr40("/month")}</>
                                    : <>{tr40("Pay")} {fmtPrice(data.package.price_cents, data.package?.currency)}</>}
                        </Button>
                        <p className="text-center text-xs text-[#1a3a5c]/65"> {tr40("Secured by Stripe. Your card details never touch SplashPad.")} </p>
                    </form>
                </Card>
            </div>
        </div>
    );
}

function InviteSuccess({ data, navigate, alreadyPaid = false }) {
    const { t: tr40 } = useLocaleR40();
    const recurring = data.kind === 'class';
    return (
        <div className="min-h-dvh bg-[#f4f7fb] p-6">
            <Card className="mx-auto max-w-lg p-8 text-center" data-testid="invite-success-card">
                <CheckCircle2 className="mx-auto mb-3 h-10 w-10 text-[#00b87a]" />
                <h1 className="font-display text-xl font-bold text-[#0a1628]">{tr40("You're enrolled! 🎉")}</h1>
                <p className="mt-2 text-sm text-[#1a3a5c]/85">
                    {alreadyPaid
                        ? `Good news — your earlier payment for "${data.package.name}" already went through, so there's nothing more to pay. Your coach will reach out about scheduling.`
                        : recurring
                            ? `Your subscription to "${data.package.name}" is active. You'll be billed automatically each month — cancel anytime from your parent dashboard.`
                            : `Your payment for "${data.package.name}" is complete. Your coach will reach out about scheduling your sessions.`}
                </p>
                {alreadyPaid && <p className="mt-1 text-xs text-[#1a3a5c]/60" data-testid="invite-already-paid-note">{tr40("No new charge was made.")}</p>}
                <Button
                    onClick={() => navigate('/parent/dashboard')}
                    className="sd-cta sd-focus mt-5 h-11 rounded-xl"
                    data-testid="invite-success-go-to-dashboard"
                > {tr40("Go to your dashboard")} </Button>
            </Card>
        </div>
    );
}
